import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return url.startsWith("http://") || url.startsWith("https://");
  } catch {
    return false;
  }
};

// Parameter definitions
const parameterDefinitions = [
  { key: "description", label: "Description" },
  { key: "category", label: "Category" },
  { key: "manufacturingLine", label: "Manufacturing Line" },
  { key: "rootCause", label: "Root Cause" },
  { key: "correctiveAction", label: "Corrective Action" },
  { key: "preventiveAction", label: "Preventive Action" },
  { key: "initiationDate", label: "Initiation Date" },
  { key: "targetDate", label: "Target Date" },
  { key: "closingDate", label: "Closing Date" },
  { key: "delayedDays", label: "Delayed Days" },
  { key: "status", label: "Status" },
  // { key: "inspectorEvidenceCount", label: "Inspector Evidence Count" },
  // { key: "vendorEvidenceCount", label: "Vendor Evidence Count" },
  { key: "zip_url", label: "Zip URL" },
];

// Default problem object
const defaultProblem = {
  description: "N/A",
  category: "N/A",
  manufacturingLine: "N/A",
  rootCause: "N/A",
  correctiveAction: "N/A",
  preventiveAction: "N/A",
  initiationDate: "N/A",
  targetDate: "N/A",
  closingDate: "N/A",
  delayedDays: "N/A",
  status: "N/A",
  inspectorEvidenceCount: 0,
  vendorEvidenceCount: 0,
};

const RootCauseAnalysisTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("RootCauseAnalysisTable props:", { data, startDate, endDate, shifts, subForm });

  // Validate and filter data
  const validData = (data || []).filter((item) => {
    const isValid = item && item.inspectionDate && item.inspectionShift && item.form_data;
    if (!isValid) {
      console.warn("Invalid data item filtered out:", item);
    }
    return isValid;
  });

  // Define static table headers
  const staticHeaders = [
    { id: "parameter", label: "Parameter" },
  ];

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(validData.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Build data map for easier lookup
  const dataMap = useMemo(() => {
    const map = {};
    validData.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) {
        console.warn("Skipping item with invalid date or shift:", item);
        return;
      }
      const key = `${date}_${shift}`;
      const problems = Array.isArray(item.form_data?.problems) ? item.form_data.problems : [];
      map[key] = problems.map((problem) => ({
        ...defaultProblem,
        ...problem,
        inspectorEvidenceCount: Array.isArray(problem.inspectorEvidence)
          ? problem.inspectorEvidence.length
          : 0,
        vendorEvidenceCount: Array.isArray(problem.vendorEvidence)
          ? problem.vendorEvidence.length
          : 0,
        // Include zip_url from top-level item
        zip_url: item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url) ? item.zip_url : "N/A",
      }));
      // Ensure at least one problem for empty or missing problems
      if (map[key].length === 0) {
        map[key] = [{ ...defaultProblem, zip_url: item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url) ? item.zip_url : "N/A" }];
      }
      console.log(`DataMap entry for ${key}:`, map[key]);
    });
    return map;
  }, [validData]);

  // Determine maximum number of problems per date-shift
  const maxProblemsPerShift = useMemo(() => {
    const maxCounts = {};
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const problemCount = (dataMap[key] || []).length;
        maxCounts[key] = Math.max(problemCount, 1);
      });
    });
    return maxCounts;
  }, [dates, effectiveShifts, dataMap]);

  // Create dynamic headers
  const dynamicHeaders = useMemo(() => {
    const headers = [];
    dates.forEach((date) => {
      if (!startDate || !endDate || (date >= formatDate(startDate) && date <= formatDate(endDate))) {
        effectiveShifts.forEach((shift) => {
          const key = `${date}_${shift}`;
          const problemCount = maxProblemsPerShift[key] || 1;
          for (let i = 0; i < problemCount; i++) {
            headers.push({
              id: `${key}_problem_${i + 1}`,
              label: `Problem ${i + 1}`,
              dateShift: `${date} ${shift}`,
              problemIndex: i,
            });
          }
        });
      }
    });
    return headers;
  }, [dates, effectiveShifts, maxProblemsPerShift, startDate, endDate]);

  // Group headers by date-shift for top-level headers
  const topLevelHeaders = useMemo(() => {
    const headers = [];
    const seen = new Set();
    dynamicHeaders.forEach((header) => {
      if (!seen.has(header.dateShift)) {
        seen.add(header.dateShift);
        const problemCount = dynamicHeaders.filter((h) => h.dateShift === header.dateShift).length;
        headers.push({
          id: header.dateShift,
          label: header.dateShift,
          colSpan: problemCount,
        });
      }
    });
    return headers;
  }, [dynamicHeaders]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: estimateTextWidth("Parameter"),
      value: 100, // Default width for value columns
    };

    // Width for parameter column
    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.min(
        Math.max(widths.parameter, estimateTextWidth(param.label)),
        8 * window.innerWidth / 100
      );
    });

    // Width for value columns based on data
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const problems = dataMap[key] || [{ ...defaultProblem }];
        const problemCount = maxProblemsPerShift[key] || 1;
        console.log(`Processing problems for ${key}:`, problems);
        for (let i = 0; i < problemCount; i++) {
          const problem = { ...defaultProblem, ...(problems[i] || {}) };
          const fields = [
            problem.description,
            problem.category,
            problem.manufacturingLine,
            problem.rootCause,
            problem.correctiveAction,
            problem.preventiveAction,
            problem.initiationDate,
            problem.targetDate,
            problem.closingDate,
            problem.delayedDays,
            problem.status,
            (problem.inspectorEvidenceCount ?? 0).toString(),
            (problem.vendorEvidenceCount ?? 0).toString(),
            problem.zip_url === "N/A" ? "N/A" : "LINK",
          ];
          fields.forEach((value) => {
            widths.value = Math.min(
              Math.max(
                widths.value,
                estimateTextWidth(`Problem ${i + 1}`),
                estimateTextWidth(value)
              ),
              8 * window.innerWidth / 100
            );
          });
        }
      });
    });

    dynamicHeaders.forEach((header) => {
      widths[header.id] = widths.value;
    });

    topLevelHeaders.forEach((header) => {
      widths[header.id] = dynamicHeaders
        .filter((h) => h.dateShift === header.id)
        .reduce((sum) => sum + widths.value, 0);
    });

    return widths;
  }, [dataMap, dynamicHeaders, topLevelHeaders, maxProblemsPerShift, dates, effectiveShifts]);

  // Calculate left position for sticky column
  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = parameterDefinitions.map((param) => ({
      parameter: param.label,
      data: {},
    }));

    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const problems = dataMap[key] || [{ ...defaultProblem }];
        const problemCount = maxProblemsPerShift[key] || 1;
        for (let i = 0; i < problemCount; i++) {
          const problemKey = `${key}_problem_${i + 1}`;
          const problem = { ...defaultProblem, ...(problems[i] || {}) };
          parameterDefinitions.forEach((param) => {
            const row = rows.find((r) => r.parameter === param.label);
            row.data[problemKey] = param.key === "inspectorEvidenceCount" || param.key === "vendorEvidenceCount"
              ? (problem[param.key] ?? 0).toString()
              : param.key === "zip_url"
              ? problem.zip_url
              : problem[param.key] || "N/A";
          });
        }
      });
    });

    return rows;
  }, [dataMap, maxProblemsPerShift, dates, effectiveShifts]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    // Row 1: Date + Shift (merged across all problems in that shift)
    const row1 = ["Parameter"];
    let colIdx = 1;

    topLevelHeaders.forEach((header) => {
      row1.push(header.label);
      if (header.colSpan > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + header.colSpan - 1 } });
      }
      colIdx += header.colSpan;
    });

    // Row 2: Problem 1, Problem 2, ...
    const row2 = ["Parameter"];
    dynamicHeaders.forEach((h) => row2.push(h.label));

    // Data rows
    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      dynamicHeaders.forEach((header) => {
        const value = row.data[header.id];

        // Make zip_url clickable in Excel
        if (row.parameter === "Evidence ZIP" && value && value !== "N/A" && value.startsWith("http")) {
          r.push(`=HYPERLINK("${value}", "DOWNLOAD")`);
        } else {
          r.push(value || "N/A");
        }
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Apply merged cells
    if (merges.length > 0) ws["!merges"] = merges;

    // Column widths
    ws["!cols"] = [
      { wch: 30 }, // Parameter
      ...Array(dynamicHeaders.length).fill({ wch: 28 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Root Cause Analysis");

    const fileName = `Root_Cause_Analysis_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Root Cause Analysis
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dynamicHeaders.length === 0}
      >
        Download as Excel
      </Button>
      
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {topLevelHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  colSpan={header.colSpan}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
            <TableRow>
              {dynamicHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={staticHeaders.length + dynamicHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {dynamicHeaders.map((header) => (
                    <BodyTableCell
                      key={`${header.id}_${index}`}
                      columnWidth={columnWidths[header.id]}
                    >
                      {row.parameter === "Zip URL" && row.data[header.id] !== "N/A" ? (
                        <a
                          href={row.data[header.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          LINK
                        </a>
                      ) : (
                        row.data[header.id]
                      )}
                    </BodyTableCell>
                  ))}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RootCauseAnalysisTable;